import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, Mail, MapPin, MessageCircle, Phone, Send } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import {
  ADDRESS,
  EMAIL,
  HOURS,
  SERVICES,
  WA_MESSAGES,
  WHATSAPP_DISPLAY,
  wa,
  track,
} from "../../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO = [
  { icon: MessageCircle, label: "WhatsApp", value: WHATSAPP_DISPLAY },
  { icon: Phone, label: "Telefone", value: WHATSAPP_DISPLAY },
  { icon: Mail, label: "E-mail", value: EMAIL },
  { icon: MapPin, label: "Morada", value: "Rua José Campas n.º 2\n1800-236 Lisboa, Portugal" },
  { icon: Clock, label: "Horário", value: "Segunda a sábado\n08:00 – 18:00" },
];

const EMPTY = { name: "", email: "", phone: "", service: "", message: "" };

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-navy placeholder:text-slate-400 outline-none transition-all duration-300 focus:border-brand focus:ring-4 focus:ring-brand/10";

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      track("quote_request", { source: "contact_form" });
      toast.success("Mensagem enviada com sucesso! Entraremos em contacto em breve.");
      setForm(EMPTY);
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error("Aguarde alguns segundos antes de enviar novamente.");
      } else {
        toast.error("Não foi possível enviar a mensagem. Tente novamente ou fale connosco no WhatsApp.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contactos" className="py-20 lg:py-28" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          number="07"
          eyebrow="Contactos"
          title="Entre em contacto"
          subtitle="Estamos disponíveis para esclarecer dúvidas e preparar o seu orçamento gratuito."
        />

        <div className="mt-14 grid gap-8 lg:grid-cols-5">
          <Reveal className="lg:col-span-2">
            <div className="flex h-full flex-col gap-4">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                {INFO.map((item) => (
                  <div
                    key={item.label}
                    data-testid={`contact-info-${item.label.toLowerCase().replace(/-/g, "")}`}
                    className="rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_4px_20px_rgba(11,25,44,0.04)]"
                  >
                    <span className="grid h-10 w-10 place-items-center rounded-xl bg-mist">
                      <item.icon className="h-5 w-5 text-brand" />
                    </span>
                    <p className="mt-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                      {item.label}
                    </p>
                    <p className="mt-1 whitespace-pre-line text-sm font-semibold text-navy">{item.value}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <a
                  href={wa(WA_MESSAGES.contacto)}
                  target="_blank"
                  rel="noopener noreferrer"
                  id="cta-contacto"
                  data-testid="cta-contacto-whatsapp"
                  data-event="whatsapp_click"
                  onClick={() => track("whatsapp_click", { location: "contact" })}
                  className="cta-whatsapp inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-whatsapp px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-whatsapp/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-whatsapp-hover"
                >
                  <MessageCircle className="h-4 w-4" />
                  Falar no WhatsApp
                </a>
                <a
                  href={`mailto:${EMAIL}`}
                  data-testid="cta-email-button"
                  data-event="email_click"
                  onClick={() => track("email_click", { location: "contact" })}
                  className="cta-email inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-3.5 text-sm font-semibold text-navy transition-all duration-300 hover:-translate-y-0.5 hover:border-brand/40 hover:text-brand"
                >
                  <Mail className="h-4 w-4" />
                  Enviar e-mail
                </a>
              </div>

              <div className="overflow-hidden rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(11,25,44,0.06)]">
                <iframe
                  title="Mapa — TopEstofos, Rua José Campas n.º 2, 1800-236 Lisboa"
                  data-testid="google-map-iframe"
                  src="https://www.google.com/maps?q=Rua%20Jos%C3%A9%20Campas%202%2C%201800-236%20Lisboa%2C%20Portugal&output=embed"
                  className="h-56 w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-3">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="h-full rounded-3xl border border-slate-100 bg-white p-6 shadow-[0_8px_40px_rgba(11,25,44,0.06)] sm:p-8"
            >
              <h3 className="text-xl font-bold tracking-tight text-navy">Peça o seu orçamento</h3>
              <p className="mt-1 text-sm text-slate-500">
                Preencha o formulário e responderemos o mais rapidamente possível.
              </p>

              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
                    Nome *
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-form-name"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={set("name")}
                    placeholder="O seu nome"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
                    E-mail *
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    data-testid="contact-form-email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="email@exemplo.pt"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
                    Telefone *
                  </label>
                  <input
                    id="contact-phone"
                    type="tel"
                    data-testid="contact-form-phone"
                    required
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="+351 ..."
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-service" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
                    Serviço
                  </label>
                  <select
                    id="contact-service"
                    data-testid="contact-form-service"
                    value={form.service}
                    onChange={set("service")}
                    className={inputClass}
                  >
                    <option value="">Selecionar serviço</option>
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.name}>
                        {s.name}
                      </option>
                    ))}
                    <option value="Outro">Outro</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-message" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
                    Mensagem *
                  </label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-form-message"
                    required
                    minLength={5}
                    rows={4}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Conte-nos o que precisa de higienizar..."
                    className={`${inputClass} resize-none`}
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="contact-form-submit"
                disabled={loading}
                className="mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-brand px-8 py-4 text-base font-semibold text-white shadow-xl shadow-brand/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-600 hover:shadow-2xl hover:shadow-brand/35 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                <Send className="h-4 w-4" />
                {loading ? "A enviar..." : "Enviar mensagem"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
